import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import * as _ from 'lodash';

// data, models, helpers
import { segmentAnimation } from '../base/animations';
import { getHeaderDescription } from 'shared/product/product-helpers';
import { getAssetThumbnail, isImageFileType } from 'shared/digital-assets/digital-asset-helpers';
import * as utils from 'utils/utils';
// components
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { SelectDropdownComponent } from 'shared/dropdown/select-dropdown/select-dropdown.component';


@Component({
  moduleId: module.id,
  selector: 'overview-segment',
  templateUrl: 'overview.component.html',
  styleUrls: ['../base/_base-segment.component.scss', 'overview.component.scss'],
  animations: [segmentAnimation],
})
export class OverviewComponent extends BaseSegmentComponent implements OnInit {

  @ViewChild(SelectDropdownComponent, {static: false})
  priceSheetsDropdown: SelectDropdownComponent | void;

  @ViewChild('slidesWrap', {static: false})
  slidesWrap: ElementRef | void;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get partNumber(): number|string {
    return _.get(this.itemDetail, 'partNumber', '');
  }

  get description(): string {
    return getHeaderDescription(this.itemDetail);
  }

  get asmDescription(): string {
    return _.get(this.itemDetail, `descriptions.ASM.${this.selectedLanguage}`, this.emptyValuePlaceholder);
  }

  get dimentionsUom(): string {
    return _.get(this.itemDetail, 'packages.EA.dimensions.uom') === 'IN'
    ? 'in' : 'cm';
  }

  get weightUom(): string {
    return _.get(this.itemDetail, 'packages.EA.weights.uom') === 'PG' ?
    'lbs' : 'kg';
  }

  get attributes(): Array<any> {
    return _.get(this.itemDetail, 'attributes');
  }

  get slidesWrapWidth(): number {
    return _.get(this.slidesWrap, 'nativeElement.offsetWidth', 0);
  }

  get visibleSlides(): number {
    return Math.floor(this.slidesWrapWidth / this.slideWidth);
  }

  get slidesLength(): number {
    return this.slideWidth * _.size(this.imageAssets);
  }

  get isNextDisabled(): boolean {
    return (this.slidesWrapWidth + this.slidesOffsetLeft >= this.slidesLength);
  }

  get isPrevDisabled(): boolean {
    return this.slidesOffsetLeft <= 0;
  }

  fields: Array<any> = [
    { title: 'Part Number ', path: 'partNumber' },
    {},
    { title: 'Part Type', path: 'partTerminology.name'},
    { title: 'Available Date', path: 'details.availableDate', format: true },
    { title: 'GTIN', path: 'details.gtin' },
    { title: 'Old Part Number', path: 'details.oldPartNumber' },
  ];

  packageFields: Array<any> = [
    { title: 'Length', path: 'packages.EA.dimensions.length'},
    { title: 'Width', path: 'packages.EA.dimensions.width'},
    { title: 'Height', path: 'packages.EA.dimensions.height'},
    { title: 'Weight', path: 'packages.EA.weights.weight'},
  ];
  selectedLanguage: string = 'EN';
  // price sheets
  priceSegments: Array<any> = [
    { title: 'Jobber', path: 'JBR' },
    { title: 'Retail', path: 'RET' },
    { title: 'Retail MAP', path: 'RMP' },
    { title: 'Distributor', path: 'WD1' },
  ];
  priceSheetsItems: Array<any> = [];
  selectedPriceSheet: any;
  // images
  assetBigImageUrl: string = '';
  imageAssets: Array<any> = [];
  selectedAsset: any;
  slideWidth: number = 87;
  slidesOffsetLeft: number = 0;
  emptyValuePlaceholder: string = '-';

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    if (this.itemDetail) {
      this.handleAllDataUpdate();
    }
    this.runAnimation('show');
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onThumbnailClick(asset: any) {
    this.selectedAsset = asset;
    this.assetBigImageUrl = getAssetThumbnail(asset, 'big');
  }

  onBigImageClick() {
    if (this.selectedAsset?.download_link) {
      window.open(this.selectedAsset.download_link, '_blank');
    }
  }

  onPriceSheetChange(priceSheet: any) {
    this.selectedPriceSheet = priceSheet;
  }

  onSlideBtnClick(direction: number) {
    if (direction === 1 && this.isNextDisabled) { return; }
    if (direction === -1 && this.slidesOffsetLeft === 0) { return; }
    this.slidesOffsetLeft += (this.visibleSlides * this.slideWidth * direction);
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  getAssetThumbnail(asset: any): string {
    return getAssetThumbnail(asset);
  }

  getFieldValue(path: string): any {
    return _.get(this.itemDetail, path);
  }

  public handleAllDataUpdate(): void {
    this.separateImageAssets();
    this.addDropdownPriceSheets();
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private separateImageAssets(): void {
    this.imageAssets = _.filter(_.get(this.itemDetail, 'digitalAssets', []), isImageFileType);
    if (this.imageAssets) {
      const primaryPhoto = _.find(this.imageAssets, (item: any) => {
        return item.asset_type === 'P04';
      });
      this.selectedAsset = primaryPhoto ? primaryPhoto : {};
      this.assetBigImageUrl = getAssetThumbnail(this.selectedAsset, 'big');
    }
  }

  private addDropdownPriceSheets(): void {
    this.priceSheetsItems = _.chain(this.priceSheets)
    .map((item: any) => {
      item.title = item.price_sheet_name;
      item.option = item.price_sheet_number;
      return item;
    })
    .sortBy('default').value();
    if (_.size(this.priceSheetsItems)) {
      this.selectedPriceSheet = this.priceSheetsItems[0];
    }
  }

  private getPrice(item: any): string | null {
    if (!this.priceSheets) { return ''; }
    if (!this.selectedPriceSheet) { return ''; }
    const currency = utils.getItemProperty(
      item,
      `prices.${this.selectedPriceSheet.price_sheet_number}.${item.path}.USD`,
      this.itemDetail
    );
    if (currency && currency.price) {
      const re = new RegExp('^-?\\d+(?:\.\\d{0,' + (2 || -1) + '})?');
      return currency.price.toFixed(2).toString().match(re)[0];
    } else {
      return this.emptyValuePlaceholder;
    }
  }

  private getPackageValue(itemName: string): string {
    const uom = itemName.indexOf('dimensions') !== -1 ? this.dimentionsUom : this.weightUom;
    return `${this.getFieldValue(itemName) || this.emptyValuePlaceholder} ${uom}`;
  }
}
